import { useCallback, useState } from 'react';

import { DEFAULT_REVEAL_CHARS_PER_SECOND, TRevealPlan, useRevealEngine } from './useRevealEngine.hook';

// Reveals HTML character by character. Slicing raw HTML would leak half-open tags, so parse once and
// walk text nodes instead.
export const useTypingRevealHtml = (sourceHtml: string, charsPerSecond: number = DEFAULT_REVEAL_CHARS_PER_SECOND) => {
  // useEffect runs post-paint; seeding with sourceHtml would flash the full answer before reveal resets to 0.
  const [html, setHtml] = useState('');

  const createPlan = useCallback((source: string): TRevealPlan | null => {
    if (!source) {
      setHtml('');
      return null;
    }

    const doc = new DOMParser().parseFromString(source, 'text/html');
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
    const segments: { node: Text; text: string }[] = [];
    let totalChars = 0;
    for (let n = walker.nextNode() as Text | null; n; n = walker.nextNode() as Text | null) {
      segments.push({ node: n, text: n.data });
      totalChars += n.data.length;
      n.data = '';
    }

    if (totalChars === 0) {
      setHtml(source);
      return null;
    }

    // Hide block markers (bullets, paragraph spacing) until each element has revealed text.
    const blocks = Array.from(
      doc.body.querySelectorAll<HTMLElement>('p, h1, h2, h3, h4, h5, h6, ul, ol, li, table, tr')
    );
    const reveal = (charsToShow: number) => {
      let remaining = Math.max(0, Math.min(totalChars, charsToShow));
      for (const { node, text } of segments) {
        const take = Math.min(text.length, remaining);
        node.data = text.slice(0, take);
        remaining -= take;
      }
      for (const el of blocks) {
        el.style.display = el.textContent ? '' : 'none';
      }
      setHtml(doc.body.innerHTML);
    };

    return { totalChars, reveal };
  }, []);

  const { isAnimating, complete } = useRevealEngine(sourceHtml, createPlan, charsPerSecond);
  return { html, isAnimating, complete };
};
